import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "../../utils/url";
import { useAuth } from "../../contexts/AuthContext";

export enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

export interface PaginationParams {
  page: number;
  pageSize: number;
  sortProperty?: string;
  sortDirection?: SortDirection;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  pageCount: number;
  hasNextPage: boolean;
}

interface QueryParam {
  key: string;
  defaultValue: string | number | SortDirection;
}

const QUERY_PARAMS: Record<string, QueryParam> = {
  page: {
    key: "page",
    defaultValue: 1,
  },
  sortField: {
    key: "sortField",
    defaultValue: "",
  },
  sortDirection: {
    key: "sortDirection",
    defaultValue: SortDirection.ASC,
  },
}

export type GetDataParams = PaginationParams;

type GetData<T> = (params: GetDataParams) => Promise<PaginatedResponse<T>>;

interface UseTableDataProps<T> {
  tableId: string; // Used to differentiate between tables on the same page
  getData: GetData<T>;
  initialSortField?: keyof T;
  initialSortDirection?: SortDirection;
  updateUrlParams?: boolean;
}

export type UseTableDataReturn<T> = {
  items: T[];
  totalPages: number;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  sortField: keyof T;
  sortDirection: SortDirection;
  handleSortClick: (field: keyof T) => void;
  loading: boolean;
  refresh: () => Promise<void>;
};

export const useTableData = <T extends Record<string, any>>({
  tableId,
  getData,
  initialSortField,
  initialSortDirection,
  updateUrlParams = true,
}: UseTableDataProps<T>): UseTableDataReturn<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, organizations } = useAuth();

  const queryParams = useMemo(() => {
    return Object.entries(QUERY_PARAMS).reduce((acc, [key, value]) => {
      acc[key] = { ...value, key: `${tableId}_${value.key}` };
      return acc;
    }, {} as typeof QUERY_PARAMS);
  }, [tableId]);

  const [items, setItems] = useState<T[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(() => (
    Number(searchParams.get(queryParams.page.key)) || Number(queryParams.page.defaultValue)
  ));
  const [sortField, setSortField] = useState(() => (
    (searchParams.get(queryParams.sortField.key) || initialSortField || queryParams.sortField.defaultValue) as keyof T
  ));
  const [sortDirection, setSortDirection] = useState(() => (
    (searchParams.get(queryParams.sortDirection.key) || initialSortDirection || queryParams.sortDirection.defaultValue) as SortDirection
  ));
  const [loading, setLoading] = useState(true);

  const setUrlParam = useCallback((params: URLSearchParams, paramDetails: QueryParam, value: any) => {
    if (!value || value === paramDetails.defaultValue) {
      params.delete(paramDetails.key);
    } else {
      params.set(paramDetails.key, String(value));
    }
  }, []);

  useEffect(() => {
    if (!updateUrlParams) return;

    const params = new URLSearchParams(searchParams);
    setUrlParam(params, queryParams.page, currentPage);
    setUrlParam(params, queryParams.sortField, sortField);
    setUrlParam(params, queryParams.sortDirection, sortDirection);
    setSearchParams(params, { replace: true });
  }, [currentPage, sortField, sortDirection, setSearchParams, setUrlParam, queryParams, updateUrlParams, searchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const paginationParams: PaginationParams = {
      page: currentPage,
      pageSize: 10,
    };
    if (sortField) {
      paginationParams.sortProperty = String(sortField);
      paginationParams.sortDirection = sortDirection;
    }

    const response = await getData(paginationParams);

    if (response) {
      if (currentPage > response.pageCount || currentPage < 0) {
        setCurrentPage(response.pageCount); // page count cannot go out of bounds
      }
      setItems(response.items);
      setTotalPages(response.pageCount);
    }
    setLoading(false);
  }, [currentPage, sortField, sortDirection, getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData, user, organizations]);

  const handleSortClick = useCallback((field: keyof T) => {
    if (sortField === field) {
      const newDirection = sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
      setSortDirection(newDirection);
    } else {
      setSortDirection(SortDirection.ASC);
      setSortField(field);
    }
  }, [sortField, sortDirection]);

  return useMemo(() => ({
    items,
    totalPages,
    currentPage,
    setCurrentPage,
    sortField,
    sortDirection,
    handleSortClick,
    loading,
    refresh: fetchData,
  }), [
    items,
    totalPages,
    currentPage,
    sortField,
    sortDirection,
    handleSortClick,
    loading,
    fetchData,
  ]);
};
